// Lesson 5: Rekap dan Percakapan Pendek - Question Configurations
export interface QuestionConfig {
    type: string;
    wordBank: number[]; // Word IDs to use
    questionSentence?: number[]; // Order of word IDs for sentence questions
    correctAnswer?: number[] | number; // Correct answer(s)
    options?: Array<{ id: number; text?: string }>; // Multiple choice options
    imagePrompt?: string; // For image-based questions
}

export interface MatchingPair {
    indonesian: string;
    batak: string;
}

// Vocabulary introduction configurations (individual words only)
export const unit1Lesson5VocabIntroConfigs = [
    {
        type: "vocab-intro",
        introWordId: 101, // Aha (Siapa)
        imagePrompt: "siapa"
    },
    {
        type: "vocab-intro",
        introWordId: 102, // Sai (Dari mana)
        imagePrompt: "dari mana"
    },
    {
        type: "vocab-intro",
        introWordId: 103, // Piro (Berapa)
        imagePrompt: "berapa"
    },
    {
        type: "vocab-intro",
        introWordId: 104, // anakkonmu (namamu)
        imagePrompt: "namamu"
    },
    {
        type: "vocab-intro",
        introWordId: 105, // asalmu
        imagePrompt: "asalmu"
    },
    {
        type: "vocab-intro",
        introWordId: 106, // tuamu (umurmu)
        imagePrompt: "umurmu"
    },
    {
        type: "vocab-intro",
        introWordId: 107, // Horas
        imagePrompt: "horas"
    },
    {
        type: "vocab-intro",
        introWordId: 108, // si
        imagePrompt: "si"
    },
    {
        type: "vocab-intro",
        introWordId: 109, // Lestari
        imagePrompt: "lestari"
    },
    {
        type: "vocab-intro",
        introWordId: 110, // Porsea
        imagePrompt: "porsea"
    }
];

// No vocab image quiz for lesson 5 - focusing on conversations

// Fill in the blank configurations - sentence questions
export const unit1Lesson5FillInBlankConfigs: QuestionConfig[] = [
    {
        type: "fill-in-the-blank",
        wordBank: [999, 114, 104],
        questionSentence: [999, 114, 104], // "______ do anakkonmu?" -> Aha
        correctAnswer: 101, // Aha
        options: [
            { id: 101, text: "Aha" }, // Aha (Siapa) - CORRECT
            { id: 102, text: "Sai" }, // Sai (Dari mana) - wrong context
            { id: 107, text: "Horas" }, // Horas (greeting) - wrong type
            { id: 115, text: "Au" }  // Au (Saya) - wrong grammar
        ]
    },
    {
        type: "fill-in-the-blank",
        wordBank: [102, 114, 999],
        questionSentence: [102, 114, 999], // "Sai do _____?" -> asalmu
        correctAnswer: 105, // asalmu
        options: [
            { id: 105, text: "asalmu" }, // asalmu - CORRECT
            { id: 104, text: "anakkonmu" }, // anakkonmu (wrong question context)
            { id: 109, text: "Lestari" }, // Lestari (name, not possessive)
            { id: 114, text: "do" }  // do (auxiliary verb, wrong grammar)
        ]
    },
    {
        type: "fill-in-the-blank",
        wordBank: [103, 114, 999],
        questionSentence: [103, 114, 999], // "Piro do _____?" -> tuamu
        correctAnswer: 106, // tuamu
        options: [
            { id: 106, text: "tuamu" }, // tuamu - CORRECT
            { id: 104, text: "anakkonmu" }, // anakkonmu (wrong question context)
            { id: 110, text: "Porsea" }, // Porsea (name, not age)
            { id: 108, text: "si" }  // si (particle, wrong grammar)
        ]
    }
];

// Compose sentence configurations
export const unit1Lesson5ComposeSentenceConfigs: QuestionConfig[] = [
    {
        type: "compose-sentences",
        wordBank: [101, 104], // Aha, do, anakkonmu
        questionSentence: [101, 114, 104], // "Aha do anakkonmu?" - What is your name?
        correctAnswer: [101, 104]
    },
    {
        type: "compose-sentences",
        wordBank: [102, 105], // Sai, do, asalmu
        questionSentence: [102, 114, 105], // "Sai do asalmu?" - Where are you from?
        correctAnswer: [102, 105]
    },
    {
        type: "compose-sentences",
        wordBank: [103, 106], // Piro, do, tuamu
        questionSentence: [103, 114, 106], // "Piro do tuamu?" - How old are you?
        correctAnswer: [103, 106]
    },
    {
        type: "compose-sentences",
        wordBank: [107, 115, 109], // Horas, Au, si, Lestari
        questionSentence: [107, 115, 108, 109], // "Horas au si Lestari" - Hello, I am Lestari
        correctAnswer: [107, 115, 109]
    }
];

// Word translation configurations - conversation elements
export const unit1Lesson5WordTranslationConfigs = [
    {
        type: "word-translation",
        wordBank: [101], // Aha (Siapa)
        correctAnswer: 101,
        options: [
            { id: 101, text: "Siapa" }, // CORRECT - question word
            { id: 115, text: "Saya" }, // Au (pronoun, not question)
            { id: 107, text: "Salam" }, // Horas (greeting, not question)
            { id: 114, text: "adalah" } // do (auxiliary verb)
        ]
    },
    {
        type: "word-translation",
        wordBank: [102], // Sai (Dari mana)
        correctAnswer: 102,
        options: [
            { id: 102, text: "Dari mana" }, // CORRECT - location question
            { id: 101, text: "Siapa" }, // Aha (person question, not location)
            { id: 116, text: "Kamu" }, // Ho (pronoun, not question)
            { id: 108, text: "si" } // si (particle, not question)
        ]
    },
    {
        type: "word-translation",
        wordBank: [103], // Piro (Berapa)
        correctAnswer: 103,
        options: [
            { id: 103, text: "Berapa" }, // CORRECT - quantity question
            { id: 112, text: "22" }, // 22 (number, not question word)
            { id: 109, text: "Lestari" }, // Lestari (name, not question)
            { id: 117, text: "dia" } // ia (pronoun, not question)
        ]
    },
    {
        type: "word-translation",
        wordBank: [104], // anakkonmu (namamu)
        correctAnswer: 104,
        options: [
            { id: 104, text: "namamu" }, // CORRECT - possessive your name
            { id: 109, text: "Lestari" }, // Lestari (specific name, not possessive)
            { id: 115, text: "Saya" }, // Au (I, not you)
            { id: 113, text: "tahun" } // taon (year, not name)
        ]
    }
];

// Word matching pairs for lesson 5 vocabulary
export const unit1Lesson5WordMatchingPairs: MatchingPair[] = [
    { indonesian: "Siapa", batak: "ᯀᯍ" }, // Aha
    { indonesian: "Dari mana", batak: "ᯘᯀᯤ" }, // Sai
    { indonesian: "Berapa", batak: "ᯇᯪᯒᯬ" }, // Piro
    { indonesian: "namamu", batak: "ᯀᯉᯃᯒᯉ᯲ᯔᯮ" }, // anakkonmu
    { indonesian: "asalmu", batak: "ᯀᯘᯇᯔᯮ" }, // asalmu
    { indonesian: "umurmu", batak: "ᯖᯮᯀᯔᯮ" }, // tuamu
    { indonesian: "Lestari", batak: "ᯞᯘᯖᯒᯤ" }, // Lestari
    { indonesian: "Porsea", batak: "ᯇᯬᯒᯘᯀ" } // Porsea
];

// Matching words configuration
export const unit1Lesson5MatchingWordsConfig = {
    type: "matching-words",
    pairs: unit1Lesson5WordMatchingPairs,
};

// Question types for lesson 5 (only those with existing components)
export const unit1Lesson5QuestionTypes = [
    "vocab-intro",
    "fill-in-the-blank",
    "compose-sentences",
    "word-translation",
    "matching-words"
];
